import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { useAuth } from '../contexts/AuthContext';
import { Users, Plus, Edit2, Trash2, Shield } from 'lucide-react';
import { userManagementAPI } from '../services/api';
import { addNotification } from '../store/slices/uiSlice';
import CreateUserModal from '../components/modals/CreateUserModal';
import EditUserModal from '../components/modals/EditUserModal';
import DeleteUserModal from '../components/modals/DeleteUserModal';
import { colors } from '../theme/colors';

const Container = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 1400px;
  margin: 1rem auto;
  min-height: calc(100vh - 150px);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${colors.primaryHover};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${colors.primaryShadow};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Thead = styled.thead`
  background: #f9fafb;
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  color: #6b7280;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  ${props => {
    switch(props.role) {
      case 'super_admin':
        return 'background: #fee2e2; color: #991b1b;';
      case 'admin':
        return 'background: #fef3c7; color: #92400e;';
      case 'lead':
        return 'background: #dbeafe; color: #1e40af;';
      case 'case_agent':
        return 'background: #d1fae5; color: #065f46;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const IconButton = styled.button`
  padding: 0.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
  border-radius: 6px;
  
  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #1f2937;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.color || '#3b82f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.span`
  font-weight: 600;
  color: #1f2937;
`;

const UserEmail = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
`;

const UserManagement = () => {
  const { isSuperAdmin, isAdmin, user: currentUser } = useAuth();
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('UserManagement: Fetching users...');
      const response = await userManagementAPI.getUsers();
      console.log('UserManagement: Received response:', response);
      console.log('UserManagement: Users data:', response.data);
      setUsers(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load users';
      setError(errorMessage);
      console.error('Error loading users:', err);
      console.error('Error response:', err.response);
      dispatch(addNotification({
        type: 'error',
        message: `Failed to load users: ${errorMessage}`,
        duration: 4000
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData) => {
    setActionLoading(true);
    try {
      await userManagementAPI.createUser(userData);
      dispatch(addNotification({
        type: 'success',
        message: `User "${userData.username}" created successfully`,
        duration: 4000
      }));
      await loadUsers();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating user:', err);
      throw err; // Re-throw to let modal handle error display
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditUser = async (userId, updates) => {
    setActionLoading(true);
    try {
      await userManagementAPI.updateUser(userId, updates);
      dispatch(addNotification({
        type: 'success',
        message: 'User updated successfully',
        duration: 4000
      }));
      await loadUsers();
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error updating user:', err);
      throw err; // Re-throw to let modal handle error display
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(true);
    try {
      await userManagementAPI.deleteUser(userId);
      dispatch(addNotification({
        type: 'success',
        message: 'User deleted successfully',
        duration: 4000
      }));
      await loadUsers();
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      throw err; // Re-throw to let modal handle error display
    } finally {
      setActionLoading(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!isSuperAdmin && !isAdmin) {
    return (
      <Container>
        <EmptyState>
          <Shield size={48} />
          <h2>Access Denied</h2>
          <p>You don't have permission to view this page.</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container data-cy="user-management-container">
      <Header>
        <Title>
          <Users size={32} />
          User Management
        </Title>
        {isSuperAdmin && (
          <Button onClick={() => setShowCreateModal(true)} data-cy="add-user-button">
            <Plus size={20} />
            Add User
          </Button>
        )}
      </Header>

      {error && (
        <div style={{ padding: '1rem', background: '#fef2f2', color: '#dc2626', borderRadius: '8px', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {loading ? (
        <EmptyState>Loading users...</EmptyState>
      ) : users.length === 0 ? (
        <EmptyState>
          <Users size={48} />
          <h2>No Users Found</h2>
          <p>Get started by adding your first user.</p>
        </EmptyState>
      ) : (
        <Table>
          <Thead>
            <tr>
              <Th>User</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              {isSuperAdmin && <Th>Actions</Th>}
            </tr>
          </Thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <Td>
                  <UserInfo>
                    <Avatar color={user.avatar_color}>
                      {getInitials(user.full_name)}
                    </Avatar>
                    <UserDetails>
                      <UserName>{user.full_name || user.username}</UserName>
                      <UserEmail>{user.email || user.username}</UserEmail>
                    </UserDetails>
                  </UserInfo>
                </Td>
                <Td>
                  <Badge role={user.role}>
                    {user.role.replace('_', ' ').toUpperCase()}
                  </Badge>
                </Td>
                <Td>
                  <Badge role={user.is_active ? 'case_agent' : 'inactive'}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td>
                  {new Date(user.created_at).toLocaleDateString()}
                </Td>
                {isSuperAdmin && (
                  <Td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <IconButton 
                        title="Edit user" 
                        onClick={() => openEditModal(user)}
                        data-cy={`edit-user-${user.username}`}
                      >
                        <Edit2 size={18} />
                      </IconButton>
                      <IconButton 
                        title="Delete user" 
                        onClick={() => openDeleteModal(user)}
                        disabled={user.id === currentUser?.id}
                        data-cy={`delete-user-${user.username}`}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </div>
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Modals */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateUser}
        loading={actionLoading}
      />

      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        currentUserId={currentUser?.id}
        onSave={handleEditUser}
        loading={actionLoading}
      />

      <DeleteUserModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        currentUserId={currentUser?.id}
        onDelete={handleDeleteUser}
        loading={actionLoading}
      />
    </Container>
  );
};

export default UserManagement;



